import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// FIX MED-001: Add animation styles to document head instead of using style jsx
const injectModalStyles = () => {
  const styleId = 'modal-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes modal-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes modal-scale-in {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      .modal-animate-fade-in {
        animation: modal-fade-in 0.2s ease-out;
      }
      .modal-animate-scale-in {
        animation: modal-scale-in 0.2s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
};

/**
 * Modal Component
 * A flexible modal wrapper with Portal support for accessibility
 */
export const Modal = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  title,
  className = ''
}) => {
  const { t } = useTranslation('common');
  const contentRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Size variants mapping
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full'
  };

  // Handle Escape key press
  const handleEscape = useCallback((event) => {
    if (closeOnEscape && event.key === 'Escape') {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((event) => {
    if (closeOnBackdrop && contentRef.current && !contentRef.current.contains(event.target)) {
      onClose();
    }
  }, [closeOnBackdrop, onClose]);

  // Focus trap - keep focus within modal
  const handleTabKey = useCallback((event) => {
    if (event.key !== 'Tab' || !contentRef.current) return;

    const focusableElements = contentRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // If shift+tab on first element, focus last
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
    }
    // If tab on last element, focus first
    else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // Store currently focused element
    previousActiveElement.current = document.activeElement;

    // Add event listeners
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTabKey);

    // Lock body scroll with position fixed (prevents scroll leaking on all devices)
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalLeft = document.body.style.left;
    const originalWidth = document.body.style.width;
    const originalOverflow = document.body.style.overflow;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = `-${scrollX}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Focus first focusable element or modal container
    setTimeout(() => {
      if (contentRef.current) {
        const firstFocusable = contentRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          contentRef.current.focus();
        }
      }
    }, 100);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTabKey);

      // Restore body scroll
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = originalLeft;
      document.body.style.width = originalWidth;
      document.body.style.overflow = originalOverflow;

      // Restore scroll position
      window.scrollTo(scrollX, scrollY);

      // FIX HIGH-002: Check if element is still in DOM before focusing
      // Restore focus to previously focused element
      if (previousActiveElement.current?.focus &&
          document.body.contains(previousActiveElement.current)) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, handleEscape, handleTabKey]);

  // FIX CRIT-001: Cleanup modal root element when component unmounts
  useEffect(() => {
    // FIX MED-001: Inject animation styles on mount
    injectModalStyles();

    return () => {
      // Only remove modal root if no modals are using it
      const modalRoot = document.getElementById('modal-root');
      if (modalRoot && modalRoot.childNodes.length === 0) {
        modalRoot.remove();
      }
    };
  }, []);

  // Don't render if not open
  if (!isOpen) return null;

  // Get or create modal root element
  let modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    modalRoot = document.createElement('div');
    modalRoot.id = 'modal-root';
    document.body.appendChild(modalRoot);
  }

  // Modal content
  const modalContent = (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 modal-animate-fade-in"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain">
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Modal content */}
          <div
            ref={contentRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'modal-title' : undefined}
            tabIndex={-1}
            className={`
              relative bg-white rounded-lg shadow-xl
              ${sizeClasses[size]}
              w-full
              transform transition-all duration-300 ease-out
              modal-animate-scale-in
              ${className}
            `}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with title and close button */}
            {(title || !closeOnBackdrop) && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                {title && (
                  <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
                    {title}
                  </h2>
                )}
                <button
                  onClick={onClose}
                  className={`
                    ${!title ? 'ml-auto' : ''}
                    text-gray-400 hover:text-gray-600 transition-colors
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded
                  `}
                  aria-label={t('modal.ariaLabels.close')}
                >
                  <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Modal body */}
            <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Render modal in portal
  return ReactDOM.createPortal(modalContent, modalRoot);
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', 'full']),
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  title: PropTypes.string,
  className: PropTypes.string
};

export default Modal;